import * as CL from '@design-system/component-library';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { dsClass } from '../../common/constants/dsClasses';
import { paths } from '../../common/constants/pathVariables';
import { OfferSubCategory } from '../../common/enums.js';
import { fetchCommercialAddonProductOfferings } from '../../common/fetch.js';
import { useDeviceOfferData, useSelectedInstallmentOption } from '../../common/hooks/useDeviceDetailsCoCo.js';
import { useHideGlobalFooter } from '../../common/hooks/useHideGlobalFooter.js';
import { corporateCustomersMsg, eurosPerMonthCapMsg, quantityMsg, shopMsg, t } from '../../common/i18n/index.js';
import { getLocalizedString, getProductOfferingPriceDataFromType } from '../../common/utils/productOfferingsUtils.js';
import { BreadCrumbs } from '../BreadCrumbs';
import { PriceType } from '../HorizontalSubscriptionCard/types.js';
import { Loading } from '../Loading';
import { ProductAvailabilityCoCo } from '../ProductAvailabilityCoCo/ProductAvailabilityCoCo.js';
import { PRODUCT_DETAILS_CLASS } from '../ProductDetails/productDetailsConstants.js';
import { getDeviceBrand, getEnergyLabelRating } from '../ProductGridCoCo/productGridUtilsCoCo.js';
import { addToShoppingBasketCoCo } from '../ShoppingBasket/shoppingBasketFunctionsCoCo.js';
import { DeviceDetailsAccessoriesCoCo } from './DeviceDetailsAccessoriesCoCo.js';
import { DeviceDetailsChargerCoCo } from './DeviceDetailsChargerCoCo.js';
import { DeviceDetailsCommercialAddonCoco } from './DeviceDetailsCommercialAddonCoco.js';
import { DeviceDetailsFooterCoCo } from './DeviceDetailsFooterCoCo.js';
import { DeviceDetailsPaymentCoCo } from './DeviceDetailsPaymentCoCo.js';
import { DUMMY_DEVICE_PICTURES, DeviceDetailsPicture } from './DeviceDetailsPictureCoCo.js';
import {
  calculateDeviceDetailCharges,
  createDeviceCartItem,
  generateCartItemId,
  getDeviceInstallmentOptions,
  getEppDeviceInstallmentTerms,
  getProductAvailability,
  isAddonSelectableForPricing,
  isEppPricingActive,
  normalizeAddonsForPricing,
  toKauppaSlug,
} from './deviceDetailsUtilsCoCo.js';
import type { ProductOfferingCartItems } from '../../common/types/shoppingBasketCoCo.js';
import type { CommercialAddonProductOfferingsResponse } from '../../generated/api/commercialAddonProductOfferingsResponse.js';
import type { ProductOfferingCoco } from '../../generated/api/productOfferingCoco.js';
import type { ProductOfferingsResponse } from '../../generated/api/productOfferingsResponse.js';
import type { BreadCrumbList } from '../BreadCrumbs/BreadCrumbs.js';

import './DeviceDetailsCoCo.scss';

interface DeviceDetailsProps {
  offer?: ProductOfferingsResponse;
  allOffers?: ProductOfferingsResponse[];
  breadCrumbPaths?: BreadCrumbList;
  onAddToBasket?: (item: ProductOfferingCartItems) => void;
}

interface ProductOfferingWithSelection extends ProductOfferingCoco {
  isChecked: boolean;
  isEppAddon: boolean;
  quantity: number;
}

type CommercialAddonOffering = ProductOfferingCoco & {
  isEppAddon: boolean;
};

const getAddonRecurringUnitPriceWithoutTax = (addon: ProductOfferingCoco): number =>
  getProductOfferingPriceDataFromType(addon.productOfferingPrice, PriceType.RECURRING_CHARGE)?.priceWithoutTax ?? 0;

const getCommercialAddonOfferingsForResponse = (
  response: CommercialAddonProductOfferingsResponse,
  eppOfferId?: string
): CommercialAddonOffering[] =>
  (response.commercialAddonProductOffering ?? []).map(offering => ({
    ...offering,
    isEppAddon: response.id === eppOfferId,
  }));

const getCommercialAddonOfferings = (
  responses: CommercialAddonProductOfferingsResponse[],
  eppOfferId?: string
): CommercialAddonOffering[] =>
  responses
    .flatMap(response => getCommercialAddonOfferingsForResponse(response, eppOfferId))
    .filter(a => a.productOfferingType?.subcategory !== OfferSubCategory.QUANTITY_BASED_ACCESSORY);

export const DeviceDetailsCoCo = ({ offer, allOffers, breadCrumbPaths, onAddToBasket }: DeviceDetailsProps = {}) => {
  const { deviceOffer, allDeviceOffers, refreshError, isRefreshing } = useDeviceOfferData({ offer, allOffers });

  if (refreshError) {
    throw refreshError;
  }

  const [selectedQuantity, setSelectedQuantity] = useState(1);

  const [nonAccessoryAddonsOfferings, setNonAccessoryAddonsOfferings] = useState<ProductOfferingWithSelection[]>([]);
  const [accessories, setAccessories] = useState<ProductOfferingCoco[]>([]);

  const installmentOptions = deviceOffer ? getDeviceInstallmentOptions(deviceOffer) : [];
  const installmentTerms = deviceOffer?.relatedEPPOffer
    ? getEppDeviceInstallmentTerms(deviceOffer?.relatedEPPOffer)
    : [];
  const [selectedInstallmentId, setSelectedInstallmentId] = useSelectedInstallmentOption(installmentOptions);
  const [selectedEppInstallmentTermId, setSelectedEppInstallmentTermId] = useState(installmentTerms[0]?.id ?? '');
  const eppPricingSelected = isEppPricingActive(selectedEppInstallmentTermId);
  const eppPricingSelectedRef = useRef(eppPricingSelected);
  const energyLabelRating = deviceOffer ? getEnergyLabelRating(deviceOffer) : null;

  useHideGlobalFooter();

  useEffect(() => {
    eppPricingSelectedRef.current = eppPricingSelected;
  }, [eppPricingSelected]);

  useEffect(() => {
    const offerId = deviceOffer?.id;
    const eppOfferId = deviceOffer?.relatedEPPOffer?.id;
    if (!offerId) {
      return;
    }

    let isCurrent = true;

    void fetchCommercialAddonProductOfferings(offerId, eppOfferId)
      .then(responses => {
        if (!isCurrent) {
          return;
        }
        const addons = getCommercialAddonOfferings(responses, eppOfferId);
        const addonsWithSelection: ProductOfferingWithSelection[] = addons.map(offering => ({
          ...offering,
          isChecked: (offering.cardinality?.defaultNum ?? 0) >= 1,
          quantity: 1,
        }));
        setNonAccessoryAddonsOfferings(normalizeAddonsForPricing(addonsWithSelection, eppPricingSelectedRef.current));
        const accessoryItems = responses
          .flatMap(r => r.commercialAddonProductOffering ?? [])
          .filter(a => a.productOfferingType?.subcategory === OfferSubCategory.QUANTITY_BASED_ACCESSORY);
        setAccessories(accessoryItems);
      })
      .catch(() => {
        if (isCurrent) {
          setNonAccessoryAddonsOfferings([]);
          setAccessories([]);
        }
      });

    return () => {
      isCurrent = false;
    };
  }, [deviceOffer?.id, deviceOffer?.relatedEPPOffer?.id]);

  useEffect(() => {
    setNonAccessoryAddonsOfferings(current => normalizeAddonsForPricing(current, eppPricingSelected));
  }, [eppPricingSelected]);

  const oneTimePrice = deviceOffer
    ? (getProductOfferingPriceDataFromType(deviceOffer.productOfferingPrice, PriceType.ONE_TIME_CHARGE)
        ?.priceWithoutTax ?? 0)
    : 0;

  const selectedAddonRecurringTotal = nonAccessoryAddonsOfferings.reduce((total, addon) => {
    if (!addon.isChecked) {
      return total;
    }
    const unitPrice = getAddonRecurringUnitPriceWithoutTax(addon);
    return total + unitPrice * Math.max(1, addon.quantity);
  }, 0);

  const { selectedInstallmentOption, selectedEppInstallmentTerm, numberOfInstallment, monthlyCharge, oneTimeCharge } =
    calculateDeviceDetailCharges({
      installmentOptions,
      selectedInstallmentId,
      eppInstallmentTerms: installmentTerms,
      selectedEppTermId: selectedEppInstallmentTermId,
      oneTimePrice,
      selectedQuantity,
      selectedAddonRecurringTotal,
      selectedAddonOneTimePrice: 0,
    });

  const onAddToCart = () => {
    if (!deviceOffer) {
      return;
    }

    const commercialAddons = nonAccessoryAddonsOfferings
      .filter(addon => isAddonSelectableForPricing(addon, eppPricingSelected))
      .map(addon => ({
        id: addon.id,
        name: { value: getLocalizedString(addon.name) },
        description: addon.description ? { value: getLocalizedString(addon.description) } : { value: '' },
        cartItemId: generateCartItemId(),
        productOfferingPrice: addon.productOfferingPrice,
        defaultLimit: addon.quantity,
        isChecked: addon.isChecked,
        isDefault: false,
        productOfferingType: addon.productOfferingType,
        productOfferingRelationship: addon.productOfferingRelationship,
      }));

    (onAddToBasket ?? addToShoppingBasketCoCo)(
      createDeviceCartItem({
        productOffering: deviceOffer,
        quantity: selectedQuantity,
        commercialAddonProductOffering: commercialAddons,
        selectedInstallmentOption,
        numberOfInstallment,
        selectedInstallmentTerm: selectedEppInstallmentTerm,
        // Persist the device image so the Thank You page can render it from the basket.
        imageUrl: DUMMY_DEVICE_PICTURES[0]?.src,
      })
    );
  };

  if (!deviceOffer) {
    return <Loading />;
  }

  const brandName = getDeviceBrand(deviceOffer);
  // TODO : the below url will be changed and kauppa will be removed from the url
  const brandPath = brandName ? `/kauppa/${toKauppaSlug(brandName)}` : undefined;
  const name = getLocalizedString(deviceOffer.name);
  const description = deviceOffer.description ? getLocalizedString(deviceOffer.description) : '';

  const handleDeviceQuantityChange = (newQuantity: number) => {
    setSelectedQuantity(newQuantity);
    setNonAccessoryAddonsOfferings(current =>
      current.map(addon => (addon.isChecked ? { ...addon, quantity: newQuantity } : { ...addon, quantity: 1 }))
    );
  };

  const handleAddonToggle = (addonId: string, checked: boolean) => {
    setNonAccessoryAddonsOfferings(current =>
      current.map(addon =>
        addon.id === addonId && isAddonSelectableForPricing(addon, eppPricingSelected)
          ? { ...addon, isChecked: checked, quantity: checked ? selectedQuantity : 1 }
          : addon
      )
    );
  };

  const handleAddonQuantityChange = (addonId: string, quantity: number) => {
    setNonAccessoryAddonsOfferings(current =>
      current.map(addon => {
        if (addon.id !== addonId) {
          return addon;
        }
        if (!isAddonSelectableForPricing(addon, eppPricingSelected)) {
          return addon;
        }
        return {
          ...addon,
          quantity: addon.isChecked ? quantity : 1,
        };
      })
    );
  };

  return (
    <section className="of-public-device-details of-public-page" id="public-device-details">
      <BreadCrumbs
        breadCrumbPaths={
          breadCrumbPaths ?? [
            { name: t.KBLX(corporateCustomersMsg), path: paths.PUBLIC_HOME },
            { name: t.BZYZ(shopMsg), path: paths.WEB_SHOP },
            { name },
          ]
        }
      />
      <div className={`${PRODUCT_DETAILS_CLASS} ${dsClass.MARGIN_TOP_5}`}>
        <CL.Grid>
          <CL.GridRow>
            <CL.GridCol colWidthL={7} colWidthM={6} colWidthS={6} colWidthXS={4}>
              <DeviceDetailsPicture />
            </CL.GridCol>
            <CL.GridCol colWidthL={5} colWidthM={6} colWidthS={6} colWidthXS={4}>
              <h1
                className={`${PRODUCT_DETAILS_CLASS}-description__header ${dsClass.MARGIN_0} ${dsClass.MARGIN_BOTTOM_3}`}
              >
                {brandPath ? (
                  <Link className={`of-public-device-details ${dsClass.H3} ${dsClass.H3_BOOK}`} to={brandPath}>
                    {brandName}
                  </Link>
                ) : (
                  <span className={`of-public-device-details ${dsClass.H3} ${dsClass.H3_BOOK}`}>{brandName}</span>
                )}
                <span className={`${PRODUCT_DETAILS_CLASS}-description__model-name ${dsClass.H3} ${dsClass.H3_BOOK}`}>
                  {` ${name}`}
                </span>
              </h1>

              {description && <p className="of-public-device-details__description">{description}</p>}
              {isRefreshing ? (
                <Loading />
              ) : (
                <DeviceDetailsPaymentCoCo
                  deviceInstallmentOptions={installmentOptions}
                  selectedDeviceInstallmentId={selectedEppInstallmentTermId ? '' : selectedInstallmentId}
                  onSelectDeviceInstallment={id => {
                    setSelectedInstallmentId(id);
                    setSelectedEppInstallmentTermId('');
                  }}
                  eppInstallmentTerms={installmentTerms}
                  selectedEppInstallmentTermId={selectedEppInstallmentTermId}
                  onSelectEppInstallmentTerm={setSelectedEppInstallmentTermId}
                />
              )}

              <div className="of-public-device-details__quantity-energy-row">
                <div className="of-public-device-details__quantity-section">
                  <CL.Quantity
                    className=""
                    currentValue={selectedQuantity}
                    i18n_quantity_deleteAriaLabel="Poista"
                    i18n_quantity_minusAriaLabel="Vähennä yksi"
                    i18n_quantity_plusAriaLabel="Lisää yksi"
                    i18n_quantity_quantityAriaLabel="Määrä"
                    label={t.M0W7(quantityMsg)}
                    minValue={1}
                    valueIncrement={1}
                    onChange={handleDeviceQuantityChange}
                  />
                </div>

                <ProductAvailabilityCoCo availability={getProductAvailability(deviceOffer)} />

                <div className="of-public-device-details__regulatory-labels">
                  {energyLabelRating ? (
                    <div className="of-public-device-details__energy-section">
                      <CL.EnergyLabel
                        className={dsClass.PADDING_LEFT_5}
                        rating={energyLabelRating}
                        size="s"
                        fluidSize
                        i18n_energyLabel_label=""
                      />
                      <div>
                        <Link to="https://eprel.ec.europa.eu/screen/product/smartphonestablets20231669/2251149">
                          {t.J1SK('Product Information Sheet')}
                        </Link>
                        <CL.Icon icon="external-link" size="s" color="primary" />
                      </div>
                    </div>
                  ) : null}
                  <DeviceDetailsChargerCoCo />
                </div>
              </div>

              {nonAccessoryAddonsOfferings.length ? (
                <div className="of-product-details-commercial-addons">
                  {nonAccessoryAddonsOfferings.map(addon => (
                    <DeviceDetailsCommercialAddonCoco
                      key={addon.id}
                      addon={addon}
                      isChecked={addon.isChecked}
                      quantity={addon.quantity}
                      maxQuantity={addon.maxLimit}
                      disabled={!isAddonSelectableForPricing(addon, eppPricingSelected)}
                      onToggle={checked => handleAddonToggle(addon.id, checked)}
                      onQuantityChange={newQuantity => handleAddonQuantityChange(addon.id, newQuantity)}
                    />
                  ))}
                </div>
              ) : null}
            </CL.GridCol>
          </CL.GridRow>
          <CL.GridRow>
            <CL.GridCol colsXL={12} colsL={12} colsM={6} colsS={6} colsXS={4}>
              <DeviceDetailsAccessoriesCoCo accessories={accessories} />
            </CL.GridCol>
          </CL.GridRow>
        </CL.Grid>
      </div>

      <DeviceDetailsFooterCoCo
        price={monthlyCharge}
        priceUnit={t.H37G(eurosPerMonthCapMsg)}
        oneTimeCharge={oneTimeCharge}
        onAddToCart={onAddToCart}
      />

      <span hidden data-total-offers={allDeviceOffers?.length} />
    </section>
  );
};
